import React, { useEffect, useState, useContext } from "react";
import api from "../../../api/axios";
import { ThemeContext } from "../../../context/ThemeContext";
import Accordion from "./Accordion";
import RedownloadModel from "../popups/RedownloadModel";
import Toast from '../../../components/common/Toast';
import { useModal } from "../../../context/ModalContext";
import "./settings.css";

export default function SettingsPage() {
  const theme = useContext(ThemeContext);
  const [template, setTemplate] = useState({});
  const [toast, setToast] = useState({ message: "", visible: false, type: 'success' });
  const [loading, setLoading] = useState(true);
  const { openModal, closeModal } = useModal();
  const [downloading, setDownloading] = useState(false);

  const COST_GROUPS = [
    { title: "Seller Costs", endpoint: "Seller Costs" },
    { title: "Short Sale", endpoint: "Short Sale" },
    { title: "FHA/VA/USDA Loans", endpoint: "loans" },
    { title: "Buyer Costs", endpoint: "Buyer Costs" },
    { title: "Refinance Costs", endpoint: "Refinance Costs" },
    { title: "Conventional Costs", endpoint: "conventional" },
  ];

  const TEMPLATE_KEY_MAP = {
    "Seller Costs": ["seller_closing_costs_section1", "seller_closing_costs_section2"],
    "Short Sale": ["shortsale_closing_costs_section1", "shortsale_closing_costs_section2"],
    "FHA/VA/USDA Loans": ["fha_closing_costs", "va_closing_costs", "usda_closing_costs"],
    "Buyer Costs": [
      "buyer_tenure_section",
      "buyer_taxes_insurance_section",
      "buyer_prepaid_section",
      "buyer_payments_section",
      "buyer_closing_costs_section",
    ],
    "Refinance Costs": ["refinance_closing_costs_section1", "refinance_closing_costs_section2"],
    "Conventional Costs": ["conventional_adjustable_section", "conventional_closing_costs_section"],
  };

  const mainId = theme?.id || "";
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const userId = user?.id;
  const companyId = user?.companyid;

  useEffect(() => {
    loadTemplate(mainId, userId, companyId);
  }, [mainId, userId, companyId]);

  async function loadTemplate(mainId, userId, companyId) {
    setLoading(true);
    try {
      const res = await api.get(`/user-template/${mainId}/${userId}/${companyId}`);
      if (res.data.status === "success" && res.data.data?.length > 0) {
        setTemplate(res.data.data[0]);
      }
    } catch {
      setTemplate({});
    } finally {
      setLoading(false);
    }
  }

  const handleUpdateToaster = ({ message, visible, type = 'success' }) => {
    setToast({ message, visible, type });
    setTimeout(() => setToast({ message: "", visible: false, type: 'success' }), 5000);
  };


  const handleDownloadClick = () => {
    openModal({
      title: "Confirm Download",
      size: "smallmodel",
      content: (
        <RedownloadModel handleConfirmDownload={handleConfirmDownload} handleCancelDownload={handleCancelDownload} downloading={downloading} />
      )
    });

  };

  const handleCancelDownload = () => {
    closeModal(false);
  };

  const handleConfirmDownload = async () => {
    setDownloading(true);
    // The confirm dialog's <RedownloadModel downloading={downloading} /> element was
    // already built (with downloading=false baked in) when handleDownloadClick ran —
    // React elements don't pick up later state changes, so that prop can never flip
    // to true on its own. Swap in a freshly-created element instead.
    closeModal();
    openModal({
      title: "Confirm Download",
      size: "smallmodel",
      content: <RedownloadModel downloading />,
    });
    try {
      // const token = localStorage.getItem("token") || localStorage.getItem("admintoken") || "";
      const res = await api.get(`/download-user-template`);

      if (res?.data?.status === "success" && res.data.data) {
        // Re-fetch the canonical template instead of trusting this endpoint's
        // own response body, so the form reflects exactly what /user-template
        // now has on the backend (same source loadTemplate uses on mount).
        await loadTemplate(mainId, userId, companyId);
        handleUpdateToaster({ message: "User settings downloaded and applied successfully.", visible: true, type: 'success' });
      } else {
        handleUpdateToaster({ message: "Failed to download user settings.", visible: true, type: 'error' });
      }
    } catch (err) {
      handleUpdateToaster({ message: "Error downloading user settings.", visible: true, type: 'error' });
    } finally {
      setDownloading(false);
      closeModal(false);
    }
  };

  const updateFee = (sectionKey, idx, key, value, subKey = null) => {

    console.log("Updating fee:", { sectionKey, idx, key, value, subKey });
    // return;
    setTemplate((prev) => {
      const updated = { ...prev };
      if (subKey) {
        const parent = { ...updated[sectionKey] };
        const nestedArr = [...(parent[subKey] || [])];
        nestedArr[idx] = { ...nestedArr[idx], [key]: value };
        parent[subKey] = nestedArr;
        updated[sectionKey] = parent;
      } else {
        const arr = [...(updated[sectionKey] || [])];
        arr[idx] = { ...arr[idx], [key]: value };
        updated[sectionKey] = arr;
      }
      return updated;
    });
  };

  const saveSection = async (sectionKey, subKey = null) => {
    try {
      let payload;
      if (subKey) {
        payload = {
          [sectionKey]: {
            [subKey]: template[sectionKey][subKey],
          },
        };
      } else {
        payload = {
          [sectionKey]: template[sectionKey],
        };
      }

      const res = await api.put(
        `/user-template/section/${sectionKey}/${mainId}/${userId}/${companyId}`,
        payload
      );
      if (res.data.status === "success")
        handleUpdateToaster({ message: "Section saved successfully!", visible: true, type: 'success' });
    } catch (err) {
      handleUpdateToaster({ message: "Error saving section.", visible: true, type: 'error' });
    }
  };

  if (loading)
    return (
      <div className="flex justify-center items-center h-64 text-gray-500">
        <div className="animate-spin h-10 w-10 border-4 border-t-blue-600 border-gray-300 rounded-full"></div>
        <span className="ml-3">Loading Settings...</span>
      </div>
    );

  return (
    <>

      <div className="settings-card">
        {/* Note Card */}
        {/*<div className="note-card">
        <div className="note-download">
          <h3>Note</h3>
          <p className="heading-b">
            If you are redownloading default costs, put a “check” in the Lock boxes that you do not want to be changed before clicking on the “Redownload default costs” tab.
          </p>
        </div>
        <div className="download-btn">
          <button onClick={() => {
            const blob = new Blob([JSON.stringify(template, null, 2)], {
              type: "application/json",
            });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `user-template.json`;
            a.click();
            URL.revokeObjectURL(url);
          }}>
            Download
          </button>
        </div>
      </div> */}

        <div className="profile-setting-haeding">
          <h2>Choose a cost type to customize</h2>
        </div>

        <Accordion
          costGroups={COST_GROUPS}
          template={template}
          templateKeyMap={TEMPLATE_KEY_MAP}
          theme={theme}
          updateFee={updateFee}
          saveSection={saveSection}
          showCommissionSection={true}
          onDownload={handleDownloadClick}
        />

      </div>
      <Toast message={toast.message} type={toast.type} visible={toast.visible} />
    </>
  );
}
